import { useNavigate } from 'react-router-dom'
import { Building2, TrendingUp, Layers, ClipboardList } from 'lucide-react'
import type { Account, Property } from '@/types/entities'
import {
  SectionCard, KpiTile, KpiGrid, HealthBadge, Badge, DataTable, EmptyState,
  type Column,
} from '@/components/ui'
import { Sparkline } from '@/components/charts'
import { C } from '@/lib/colors'
import { money, num, pct } from '@/lib/format'
import type { AccountDerived } from '../lib'
import { latestRevenue, latestOcc, locStr, occSeries } from '../lib'

export default function OverviewTab({ account, d }: { account: Account; d: AccountDerived }) {
  const navigate = useNavigate()
  const props = d.properties

  const ownedCount = props.filter((p) => p.relationshipType === 'Owned').length
  const managedCount = props.filter((p) => p.relationshipType === 'Managed-3PM').length
  // NRSF-weighted occupancy, matching AccountsList / Home / FundDetail.
  // Accumulate NRSF in the denominator ONLY when that property has a latest occupancy value.
  let occWeighted = 0
  let occWeight = 0
  for (const p of props) {
    const occ = latestOcc(p)
    const w = p.nrsf ?? 0
    if (occ != null && w > 0) { occWeighted += occ * w; occWeight += w }
  }
  const avgOcc = occWeight > 0 ? occWeighted / occWeight : undefined

  const columns: Column<Property>[] = [
    {
      key: 'name', header: 'Property', sortValue: (p) => p.name,
      render: (p) => (
        <div className="aw-cell-stack">
          <span className="aw-link">{p.name}</span>
          <span className="aw-cell-sub">{locStr(p)}</span>
        </div>
      ),
    },
    {
      key: 'lcode', header: 'Lcode', width: 78,
      sortValue: (p) => p.externalIds?.lcode ?? '',
      render: (p) => <span className="aw-mono">{p.externalIds?.lcode ?? '—'}</span>,
    },
    {
      key: 'occ', header: 'Occupancy', align: 'right', width: 120,
      sortValue: (p) => latestOcc(p) ?? -1,
      render: (p) => {
        const series = occSeries(p)
        return (
          <div className="aw-occ-cell">
            {series.length > 1 && <Sparkline data={series} width={56} height={20} color={C.blue60} fill={false} strokeWidth={1.5} />}
            <span className="num">{pct(latestOcc(p))}</span>
          </div>
        )
      },
    },
    {
      key: 'rev', header: 'In-Place Revenue', align: 'right', width: 140,
      sortValue: (p) => latestRevenue(p) ?? -1,
      render: (p) => <span className="num">{money(latestRevenue(p))}<span className="aw-unit">/mo</span></span>,
    },
    {
      key: 'status', header: 'Status', align: 'right', width: 130,
      render: (p) => p.health ? <HealthBadge health={p.health} /> : <Badge tone="neutral" variant="soft">{p.stabilization ?? p.state}</Badge>,
    },
  ]

  return (
    <div className="aw-grid-2col">
      <div className="aw-col-main">
        <KpiGrid cols={4}>
          <KpiTile label="Properties" value={num(props.length)} sub={`${ownedCount} owned · ${managedCount} managed`} onClick={() => navigate(`/accounts/${account.id}/properties`)} />
          <KpiTile label="Wtd. Occupancy" value={pct(avgOcc)} accent onClick={() => navigate(`/accounts/${account.id}/properties`)} />
          <KpiTile label="In-Place Revenue" value={money(d.revenue, { compact: true })} unit="/mo" onClick={() => navigate(`/accounts/${account.id}/financials`)} />
          <KpiTile label="Open Items" value={num(d.openItems.length)} sub={d.openItems.length ? 'Needs attention' : 'All clear'} onClick={() => navigate(`/accounts/${account.id}/action-items`)} />
        </KpiGrid>

        <SectionCard
          eyebrow="Portfolio"
          title={<>Portfolio <b>Snapshot</b></>}
          right={<span className="aw-count-pill">{props.length} assets</span>}
          pad={false}
        >
          {props.length ? (
            <DataTable<Property>
              columns={columns}
              rows={props}
              getRowKey={(p) => p.id}
              onRowClick={(p) => navigate(`/properties/${p.id}`)}
              initialSort={{ key: 'rev', dir: 'desc' }}
              dense
            />
          ) : (
            <div style={{ padding: 'var(--sp-6)' }}>
              <EmptyState icon={<Building2 size={32} />} title="No properties on this account" hint="Owned or managed properties will appear here once associated." />
            </div>
          )}
        </SectionCard>
      </div>

      <div className="aw-col-side">
        {d.insight && (
          <SectionCard eyebrow="WISE Portfolio Summary" title={<>Account <b>Verdict</b></>}>
            <div className="aw-verdict-row">
              <HealthBadge health={d.insight.verdict} />
              {d.insight.openItems && (
                <span className="aw-verdict-meta">
                  {d.insight.openItems.total} open · {d.insight.openItems.overdue} overdue
                </span>
              )}
            </div>
            <p className="aw-narrative aw-narrative--clamp">{d.insight.narrative}</p>
          </SectionCard>
        )}

        <SectionCard eyebrow="At a Glance" title={<>Account <b>Profile</b></>}>
          <div className="aw-facts">
            <Fact icon={<Layers size={15} />} label="Type" value={`${account.type} · ${account.status}`} />
            <Fact icon={<Building2 size={15} />} label="Tracks" value={d.tracks.map((t) => t.name).join(' · ') || '—'} />
            <Fact icon={<TrendingUp size={15} />} label="Health" value={<HealthBadge health={account.metrics?.health} />} />
            <Fact icon={<ClipboardList size={15} />} label="Contacts" value={`${d.contacts.length} on record`} />
          </div>
          {account.description && <p className="aw-narrative">{account.description}</p>}
        </SectionCard>
      </div>
    </div>
  )
}

function Fact({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="aw-fact">
      <span className="aw-fact-icon">{icon}</span>
      <span className="aw-fact-label label">{label}</span>
      <span className="aw-fact-value">{value}</span>
    </div>
  )
}
